import { ConfirmModal } from '@sparklayer-io/spark-dash-ui/components/confirm-modal';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Heading, Icon, Menu, Text, Tooltip } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import { getAcceptLanguage } from '../../../utils/accept-language';
import { dateTimeFormatter } from '../../../utils/format-date';
import { useThemeEditor } from '../context/theme-editor-context';
import { useThemeEditorApi } from '../hooks/use-theme-editor-api';
import type { FileType } from '../types/theme';
import { CodeEditor } from './code-editor';

function getPreviewQueryKey({
  themeId,
  type,
  slug,
  fileId,
  acceptLanguage,
}: {
  themeId: string;
  type: FileType;
  slug: string;
  fileId: string;
  acceptLanguage: string;
}) {
  return ['theme-preview', themeId, type, slug, fileId, acceptLanguage] as const;
}

export function EditorPane({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  const {
    theme,
    openFiles,
    tabOrder,
    focusedTab,
    focusedFile,
    loading,
    setFocusedTab,
    closeFile,
    updateContents,
    saveFocused,
    deleteFocused,
    changeFileVersion,
  } = useThemeEditor();

  const { themeEditorApi } = useThemeEditorApi();
  const { notificationHandler } = useHandlers();
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState<boolean>(false);
  const [closingTab, setClosingTab] = useState<{ id: string; slug: string } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canPreview = focusedFile?.type === 'pdf' || focusedFile?.type === 'email';

  if (tabOrder.length === 0) {
    return (
      <section className="flex h-full flex-1 flex-col items-center justify-center gap-2 bg-white">
        <Icon name="document" size="xl" />
        <Heading level="h3" style="md">
          {t('features:themes.pages.theme_editor_page.open_a_file')}
        </Heading>
        <Text variant="secondary">
          {t('features:themes.pages.theme_editor_page.open_a_file_hint')}
        </Text>
        {/* The sidebar is an off-canvas drawer on mobile, so show a button to open it. */}
        <Button icon="document" className="mt-2 lg:hidden" onClick={onOpenSidebar}>
          {t('features:themes.pages.theme_editor_page.open_files')}
        </Button>
      </section>
    );
  }

  async function handleSave() {
    await saveFocused();
  }

  async function openPreview() {
    if (!theme || !focusedFile?.id) {
      return;
    }
    // Capture the narrowed values before the async closures below (TS widens
    // `focusedFile.id` back to `string | undefined` inside nested functions).
    const themeId = theme.id;
    const { id: fileId, type, slug } = focusedFile;
    const isPdf = type === 'pdf';
    // Open the tab synchronously so the browser treats it as user-initiated and
    // doesn't block the popup; we point it at the rendered file once it's ready.
    const tab = window.open('', '_blank');
    setOpening(true);
    try {
      // Render through the query cache (keyed by file revision + language) so
      // re-opening an unchanged file is instant instead of re-rendering server-side.
      const blob = await queryClient.fetchQuery({
        queryKey: getPreviewQueryKey({
          themeId,
          type,
          slug,
          fileId,
          acceptLanguage: getAcceptLanguage(),
        }),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: 30 * 60 * 1000,
        retry: false,
        queryFn: async () => {
          const data = await themeEditorApi.preview({ themeId, type, slug, fileId, isPdf });
          return new Blob([data], { type: isPdf ? 'application/pdf' : 'text/html' });
        },
      });
      // Fresh object URL per open; only the blob is cached, so revoking one URL
      // never affects the cached blob or other open tabs.
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      // Release the object URL once the new tab has had time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      tab?.close();
      notificationHandler.error(t('features:themes.pages.theme_editor_page.preview.error'));
    } finally {
      setOpening(false);
    }
  }

  function handleCloseTab(id: string) {
    const file = openFiles[id];
    if (file?.unsavedChanges) {
      setClosingTab({ id, slug: file.slug });
      return;
    }
    closeFile(id);
  }

  async function handleDelete() {
    const error = await deleteFocused();
    if (error) {
      setDeleteError(error);
      return;
    }
    setDeleteError(null);
    setDeleteOpen(false);
  }

  function renderActions() {
    const previewButton = (
      <Button
        variant="secondary"
        className="shrink-0"
        icon="externalLink"
        disabled={loading || !focusedFile?.id || focusedFile?.unsavedChanges}
        loading={opening}
        onClick={() => void openPreview()}
      >
        {t('features:themes.pages.theme_editor_page.preview.open')}
      </Button>
    );

    return (
      <>
        {canPreview &&
          (focusedFile?.unsavedChanges ? (
            <Tooltip content={t('features:themes.pages.theme_editor_page.preview.unsaved_hint')}>
              {previewButton}
            </Tooltip>
          ) : (
            previewButton
          ))}

        <Menu
          align="end"
          buttonText={t('features:themes.pages.theme_editor_page.revisions')}
          disabled={loading}
          items={
            focusedFile?.history.map((revision) => ({
              key: revision.id,
              label: dateTimeFormatter.format(new Date(revision.created_at)),
              icon: revision.id === focusedFile.id ? 'check' : undefined,
              onClick: () => void changeFileVersion(revision),
            })) ?? []
          }
        />

        <Button
          variant="secondary"
          className="shrink-0"
          disabled={loading}
          onClick={() => {
            setDeleteError(null);
            setDeleteOpen(true);
          }}
        >
          {t('buttons:delete')}
        </Button>
        <Button
          className="shrink-0"
          disabled={loading || !focusedFile?.unsavedChanges}
          onClick={() => void handleSave()}
        >
          {t('buttons:save')}
        </Button>
      </>
    );
  }

  return (
    <section className="relative flex h-full min-w-0 flex-1 flex-col">
      <header className="relative z-10">
        <div className="flex items-center gap-2 overflow-x-auto border-spark-border-default border-b p-2 lg:hidden">
          {renderActions()}
        </div>

        <nav className="relative flex items-center justify-between border-spark-border-default border-b bg-white">
          <Button
            variant="bare"
            icon="document"
            className="ms-1 lg:hidden"
            ariaLabel={t('features:themes.pages.theme_editor_page.open_files')}
            onClick={onOpenSidebar}
          />
          <div className="flex min-w-0 flex-1 overflow-x-auto">
            {tabOrder.map((id) => {
              const file = openFiles[id];
              if (!file) {
                return null;
              }
              const isActive = id === focusedTab;
              return (
                <div
                  key={id}
                  className={cn(
                    'flex shrink-0 items-center border-spark-border-default border-e border-b-2 ps-3 pe-1.5',
                    isActive
                      ? 'border-b-platform-primary'
                      : 'border-b-transparent hover:bg-gray-50',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setFocusedTab(id)}
                    className="flex items-center gap-2 py-3"
                  >
                    {file.unsavedChanges && (
                      <span className="size-2 shrink-0 rounded-full bg-platform-primary" />
                    )}
                    <Text variant={isActive ? 'primary' : 'secondary'} truncate>
                      {file.slug}
                    </Text>
                  </button>
                  <Button
                    variant="bare"
                    icon="x"
                    size="xs"
                    ariaLabel={t('features:themes.pages.theme_editor_page.close_file')}
                    onClick={() => handleCloseTab(id)}
                  />
                </div>
              );
            })}
          </div>

          <div className="m-2 hidden shrink-0 flex-row items-center gap-3 lg:flex">
            {renderActions()}
          </div>
        </nav>
      </header>

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          {focusedFile && focusedTab && (
            <CodeEditor
              value={focusedFile.contents}
              fileType={focusedFile.type}
              onChange={(value) => updateContents(focusedTab, value)}
              onSave={() => void handleSave()}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={closingTab !== null}
        onClose={() => setClosingTab(null)}
        onConfirm={() => {
          if (closingTab) {
            closeFile(closingTab.id);
          }
          setClosingTab(null);
        }}
        title={t('features:themes.pages.theme_editor_page.unsaved_changes')}
        confirmText={t('features:themes.pages.theme_editor_page.close_file')}
        message={t('features:themes.pages.theme_editor_page.close_unsaved_message', {
          slug: closingTab?.slug ?? '',
        })}
      />

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
        loading={loading}
        title={t('features:themes.pages.theme_editor_page.delete_file_title')}
        confirmText={t('buttons:delete')}
        message={t('features:themes.pages.theme_editor_page.delete_file_message', {
          slug: focusedFile?.slug ?? '?',
        })}
      >
        {deleteError && <Text variant="secondary">{deleteError}</Text>}
      </ConfirmModal>
    </section>
  );
}
